import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table, Tag } from 'antd'
import { TablePaginationConfig } from 'antd/es/table'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Tank, TankMethodType } from '@/types'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { GhostButton } from '../../style'
import { CustomRowTable } from './styles'

type Props = {
  dataSource: Tank[] | undefined
  getTanksLoading: boolean
  onDelete: (id: Tank['id']) => void
  page: number
  pageSize: number
  totalItems: number
  onChange: (config: TablePaginationConfig) => void
}

const TanksTable = ({ dataSource, getTanksLoading, onDelete, page, pageSize, totalItems, onChange }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const renderMethod = (value: TankMethodType) => {
    switch (value) {
      case TankMethodType.MANUAL:
        return <Tag color="magenta">{t('components.pages.tanks.list.table.manual')}</Tag>
      case TankMethodType.AUTOMATIC:
        return <Tag color="lime">{t('components.pages.tanks.list.table.automatic')}</Tag>
      default:
        null
    }
  }

  const columns: ColumnsType<Tank> = [
    {
      title: t('components.pages.tanks.list.table.tankNo'),
      dataIndex: 'tankNo',
      key: 'tankNo',
      fixed: 'left',
      render: (record: string) => {
        return <CustomRowTable type="right">{record}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap',
    },
    {
      title: t('components.pages.tanks.list.table.detergentName'),
      render: (record: Tank) => {
        return <CustomRowTable type="left">{record.detergents[0] ? record.detergents[0].name : ''}</CustomRowTable>
      },
      fixed: 'left',
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.method'),
      dataIndex: 'method',
      key: 'method',
      render: (record: TankMethodType) => {
        return <CustomRowTable type="left">{renderMethod(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.netWeightMl'),
      render: (record: Tank) => {
        return (
          <CustomRowTable type="right">
            {record.detergents[0] ? formatJapaneseNumber(record.detergents[0].netWeightMl) : ''}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.thresholdQuantity'),
      dataIndex: 'thresholdQuantity',
      key: 'thresholdQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.remainingQuantity'),
      render: (record: Tank) => {
        const isWarning = record.remainingQuantity <= record.thresholdQuantity
        return (
          <CustomRowTable type="right" isWarning={isWarning}>
            {formatJapaneseNumber(record.remainingQuantity)}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.tanks.list.table.usedQuantity'),
      dataIndex: 'usedQuantity',
      key: 'usedQuantity',
      render: (record: number) => {
        return <CustomRowTable type="right">{record ? formatJapaneseNumber(record) : 0}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (text: string, record: Tank) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <GhostButton size="small" onClick={() => goToDetailTankPage(record.id)}>
                  {t('components.pages.admins.list.table.detail')}
                </GhostButton>
                <Button size="small" onClick={() => goToEditTankPage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditTankPage = (id: Tank['id']) => {
    const path = routes.updateTank.path.replace(':id', id.toString())
    navigate(path)
  }

  const goToDetailTankPage = (id: Tank['id']) => {
    const path = routes.tankHistories.path.replace(':id', id.toString())
    navigate(path)
  }

  return (
    <>
      <Table
        rowKey="id"
        bordered
        dataSource={dataSource}
        columns={columns}
        loading={getTanksLoading}
        pagination={{
          pageSize,
          current: page,
          total: totalItems,
          showSizeChanger: false,
        }}
        onChange={onChange}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
    </>
  )
}

TanksTable.displayName = 'TanksTable'
export default TanksTable
